import React, { useState } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useComments } from '@/hooks/social/useComments';
import { useAuth } from '@/contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import { nb, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

interface InnspillProps {
  interviewId: string;
}

const Innspill: React.FC<InnspillProps> = ({ interviewId }) => {
  const [comment, setComment] = useState('');
  const { isAuthenticated } = useAuth();
  const { t, i18n } = useTranslation();
  const { 
    comments, 
    isLoading, 
    createComment, 
    isCreating 
  } = useComments(interviewId);

  const handleSubmit = async () => {
    if (!comment.trim()) return;
    
    await createComment({
      content: comment.trim(),
      interview_id: interviewId,
      comment_type: 'feedback'
    });
    setComment('');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      handleSubmit();
    }
  };

  if (isLoading) {
    return (
      <div className="mt-4">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          <div className="h-20 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <div className="mb-6 p-4 bg-muted/30 rounded-lg border-l-4 border-primary">
        <p className="text-sm text-muted-foreground leading-relaxed">
          {t('input.description')}
        </p>
      </div>
      
      <div className="flex justify-between items-center mb-4">
        <h4 className="font-medium">{t('input.title', { count: comments.length })}</h4>
      </div>
      
      {isAuthenticated ? (
        <div className="space-y-4 mb-6">
          <Textarea
            placeholder={t('input.placeholder')}
            maxLength={500}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            onKeyDown={handleKeyPress}
            rows={3}
          />
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">
              {t('common.pressCtrlEnter', 'Press Ctrl+Enter to send')}
            </span>
            <Button 
              onClick={handleSubmit}
              disabled={!comment.trim() || isCreating}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {isCreating ? t('input.sending') : t('input.sendInput')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="mb-6 p-4 bg-muted/30 rounded-lg text-center">
          <p className="text-muted-foreground">
            {t('input.loginRequired')}
          </p>
        </div>
      )}

      {/* Comments list */}
      <div className="space-y-4">
        {comments.map((comment) => (
          <div key={comment.id} className="border rounded-lg p-4 bg-background">
            <div className="flex justify-between items-start mb-2">
              <div className="flex items-center gap-2">
                <span className="font-medium">
                  {comment.profiles?.display_name || t('common.anonymous', 'Anonymous')}
                </span>
              </div>
              <span className="text-xs text-muted-foreground">
                {formatDistanceToNow(new Date(comment.created_at), { 
                  addSuffix: true, 
                  locale: i18n.language === 'no' ? nb : enUS 
                })}
              </span>
            </div>
            <p className="text-sm leading-relaxed">{comment.content}</p>
          </div>
        ))}
        
        {comments.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <p>{t('input.noInputs')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Innspill;